// frontend/modules/service-metrics/components/ServiceSummaryStats.tsx
import React from 'react';
import { Card, Row, Col, Statistic, Typography } from 'antd';
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    ThunderboltOutlined,
    StopOutlined,
    ClockCircleOutlined,
    ApiOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import type { ServiceMetricSummaryDTO } from '@/shared/api/model';

const { Text } = Typography;

interface ServiceSummaryStatsProps {
    summary: ServiceMetricSummaryDTO | null;
    loading?: boolean;
}

const ServiceSummaryStats: React.FC<ServiceSummaryStatsProps> = ({ summary, loading }) => {
    const { dark } = useTheme();

    if (!summary) {
        return (
            <Card loading={loading} className={dark ? 'bg-gray-800' : ''}>
                <Text type="secondary">Select a service to view statistics</Text>
            </Card>
        );
    }

    const successRate = summary.successRate ?? 0;
    const successRateColor = successRate >= 95 ? '#52c41a' : 
                            successRate >= 80 ? '#faad14' : '#ff4d4f';
    const startDate = summary.startDate ? new Date(summary.startDate).toLocaleDateString() : '';
    const endDate = summary.endDate ? new Date(summary.endDate).toLocaleDateString() : '';

    return (
        <Card 
            title={`${summary.serviceName} Statistics`}
            loading={loading}
            className={dark ? 'bg-gray-800' : ''}
            extra={
                <Text type="secondary">
                    {startDate} - {endDate}
                </Text>
            }
        >
            <Row gutter={[16, 16]}>
                <Col xs={12} sm={8} md={4}>
                    <Statistic
                        title="Total Requests"
                        value={summary.totalRequests}
                        prefix={<ApiOutlined />}
                    />
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Statistic
                        title="Successful"
                        value={summary.successfulRequests}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                    />
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Statistic
                        title="Failed"
                        value={summary.failedRequests}
                        valueStyle={{ color: (summary.failedRequests ?? 0) > 0 ? '#ff4d4f' : undefined }}
                        prefix={<CloseCircleOutlined />}
                    />
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Statistic
                        title="Rate Limited"
                        value={summary.rateLimitedRequests}
                        valueStyle={{ color: (summary.rateLimitedRequests ?? 0) > 0 ? '#faad14' : undefined }}
                        prefix={<StopOutlined />}
                    />
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Statistic
                        title="CB Trips"
                        value={summary.circuitBreakerTrips}
                        valueStyle={{ color: (summary.circuitBreakerTrips ?? 0) > 0 ? '#ff4d4f' : undefined }}
                        prefix={<ThunderboltOutlined />}
                    />
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Statistic
                        title="Success Rate"
                        value={successRate}
                        precision={2}
                        suffix="%"
                        valueStyle={{ color: successRateColor }}
                    />
                </Col>
            </Row>
            
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={12} sm={8} md={6}>
                    <Statistic
                        title="Avg Response"
                        value={summary.averageResponseTimeMs}
                        precision={0}
                        suffix="ms"
                        prefix={<ClockCircleOutlined />}
                    />
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Statistic
                        title="Min Response"
                        value={summary.minResponseTimeMs}
                        suffix="ms"
                    />
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Statistic
                        title="Max Response"
                        value={summary.maxResponseTimeMs}
                        suffix="ms"
                    />
                </Col>
            </Row>
        </Card>
    );
};

export default ServiceSummaryStats;
